import React, { useState, useRef, useEffect } from 'react'
import { Link, useHistory, useLocation } from 'react-router-dom'
import { CopyToClipboard } from 'react-copy-to-clipboard'
import { ExclamationIcon, LocationMarkerIcon, DotsHorizontalIcon, DuplicateIcon, PencilIcon, CheckCircleIcon } from '@heroicons/react/solid'

import { useGlobalState } from '@/state'
import useQuery from '@/hooks/useQuery'
import useDropdown from '@/hooks/useDropdown'
import useModal from '@/hooks/useModalV2'
import Modal from '@/shared/Modal'
import SVGIcon from '@/components/icon'
import classNames from '@/utils/classNamesLocal'
import FlagModal from '@/shared/FlagModal'
import DirectMessageModal from '@/shared/DirectMessageModal'
import RecommendationModal from '@/shared/RecommendationModal'
import { MobileAd, InContentAd } from '@/Layout/Ads'

import PublicProfileGeneral from '@/pages/Users/PublicProfile/PublicProfileGeneral'
import PublicProfileSubmissions from '@/pages/Users/PublicProfile/PublicProfileSubmissions'
import PublicFollowers from '@/pages/Users/PublicProfile/PublicFollowers'
import PublicFollowings from '@/pages/Users/PublicProfile/PublicFollowings'
import PublicProjects from '@/pages/Users/PublicProfile/PublicProjects'
import Sidebar from '@/pages/Users/PublicProfile/Sidebar'

export const KIND_LABELS = {
  voiceActor: 'Voice Actor', liveActor: 'Actor', singer: 'Singer', artist: 'Artist', animator: 'Animator',
  videoEditor: 'Video Editor', musicComposer: 'Music Composer', audioEngineer: 'Audio Engineer',
  writer: 'Writer', director: 'Director', agent: 'Agent', producer: 'Producer'
}
export const KIND_ICONS = {
  voiceActor: 'voiceactor', liveActor: 'actor', singer: 'singer', artist: 'artist', animator: 'animator',
  videoEditor: 'video-editor', musicComposer: 'musiccomposer', audioEngineer: 'audio-engineer',
  writer: 'writer', director: 'director', agent: 'agent', producer: 'producer'
}

const TABS = ['Profile', 'Submissions', 'Projects']

const plain = (html) => (html || '').replace(/<[^>]*>/g, '')

// Options menu: copy link, report, block. The three real actions, Message,
// Follow, and Recommend, stay as buttons in the header.
const MoreMenu = ({ user, blocking }) => {
  const [, setToast] = useGlobalState('toast')
  const { putpostRequest } = useQuery()
  const [blocked, setBlocked] = useState(blocking)
  const dropdownRef = useRef(false)
  const { dropdownOpen, setDropdownOpen, openDropdown } = useDropdown(dropdownRef)

  const block = () => {
    putpostRequest(`/api/v3/blocked_users/${user.id}/block`, 'POST', {}, (err) => {
      if (err) {
        if (typeof err === 'string') {
          setToast(<div className='ml-3 w-0 flex-1 pt-0.5'><p className='text-sm font-medium text-red-500'>Hol up</p><p className='mt-1 text-sm text-gray-500 dark:text-gray-100'>{err}</p></div>)
        }
        return
      }
      setBlocked(!blocked)
      setDropdownOpen(false)
      setToast(<div className='ml-3 w-0 flex-1 pt-0.5'>
        <p className='text-sm font-medium text-gray-800 dark:text-white'>{ !blocked ? 'Blocked' : 'Unblocked' }</p>
        <p className='mt-1 text-sm text-gray-500 dark:text-gray-100'>{ !blocked ? "They can't message you, comment on your submissions, or see your profile anymore." : 'They can see you again.' }</p>
      </div>)
    })
  }

  const copied = () => setToast(<div className='ml-3 w-0 flex-1 pt-0.5'><p className='text-sm font-medium text-green-600 dark:text-green-300'>Copied</p><p className='mt-1 text-sm text-green-500 dark:text-green-300'>Profile link copied. Go share this person with the world.</p></div>)

  const item = 'flex w-full items-center gap-x-2 whitespace-nowrap px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 dark:text-gray-100 dark:hover:bg-gray-700'

  return <div className='relative'>
    <button type='button' onClick={openDropdown} aria-label='More options' aria-haspopup='menu' aria-expanded={dropdownOpen}
      className='inline-flex h-10 w-10 items-center justify-center rounded-full border border-gray-300 bg-white text-gray-500 shadow-sm hover:bg-gray-50 hover:text-gray-900 focus:outline-none focus:ring-2 focus:ring-cccblue dark:border-gray-600 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700'>
      <DotsHorizontalIcon className='h-5 w-5' />
    </button>
    {/* Always mounted, hidden by class: the Flag item owns its modal, and
        unmounting the menu on click would take the modal down with it. */}
    <div ref={dropdownRef} role='menu' className={classNames('absolute right-0 z-40 mt-1 w-64 overflow-hidden rounded-xl border border-gray-200 bg-white py-1 shadow-xl dark:border-gray-500 dark:bg-gray-900', !dropdownOpen && 'hidden')}>
      <CopyToClipboard text={`https://www.castingcall.club/m/${user.username}`} onCopy={() => { setDropdownOpen(false); copied() }}>
        <button type='button' className={item}><DuplicateIcon className='h-5 w-5 text-gray-400' /> Copy profile link</button>
      </CopyToClipboard>
      <div className='my-1 border-t border-gray-100 dark:border-gray-700' />
      <FlagModal user={user} callback={() => setDropdownOpen(false)} />
      <button type='button' onClick={block} className={classNames(item, blocked && 'text-red-600 dark:text-red-400')}>
        <ExclamationIcon className='h-5 w-5 text-gray-400' /> { blocked ? 'Unblock' : 'Block' }
      </button>
    </div>
  </div>
}

const PublicProfile = () => {
  const [profileContainer] = useGlobalState('profileContainer')
  const [currentUser] = useGlobalState('currentUser')
  const history = useHistory()
  const location = useLocation()
  const { putpostRequest } = useQuery()
  const [, setToast] = useGlobalState('toast')
  const { user, isFollowing, blocked, blocking, userMeta } = profileContainer
  const [following, setFollowing] = useState(isFollowing)
  const followersModal = useModal()
  const followingModal = useModal()

  // Tab lives in the URL so a producer can link straight to someone's submissions.
  const params = new window.URLSearchParams(location.search)
  const currentTab = TABS.includes(params.get('tab')) ? params.get('tab') : 'Profile'
  const setTab = (tab) => history.replace({ pathname: location.pathname, search: tab === 'Profile' ? '' : `?tab=${tab}` })

  useEffect(() => { setFollowing(isFollowing) }, [isFollowing])

  const isOwner = currentUser?.id === user.id
  const kinds = (userMeta || []).filter((k) => KIND_LABELS[k])

  const follow = () => {
    putpostRequest(`/api/v3/follows/${user.id}/follow_user`, 'POST', {}, (err) => {
      if (err) {
        if (typeof err === 'string') {
          setToast(<div className='ml-3 w-0 flex-1 pt-0.5'><p className='text-sm font-medium text-red-500'>Hol up</p><p className='mt-1 text-sm text-gray-500 dark:text-gray-100'>{err}</p></div>)
        }
        return
      }
      setFollowing(!following)
      setToast(<div className='ml-3 w-0 flex-1 pt-0.5'>
        <p className='text-sm font-medium text-gray-800 dark:text-white'>{ !following ? 'Following!' : 'Unfollowed' }</p>
        <p className='mt-1 text-sm text-gray-500 dark:text-gray-100'>{ !following ? "Their activity shows up in your dashboard now." : "You won't see their activity anymore." }</p>
      </div>)
    })
  }

  if (blocked) {
    return <main className='flex items-center justify-center'><h3 className='mt-5 text-gray-900 dark:text-gray-100'>This person has blocked you.</h3></main>
  }

  const counts = { Submissions: user.auditionsCount, Projects: user.projectsCount }

  return <div key={`profile${user.id}`}>
    { currentUser?.admin && user.banned && (
      <div className='mb-3 flex items-start gap-2 rounded-md bg-red-600 p-3 text-white shadow'>
        <ExclamationIcon className='mt-0.5 h-5 w-5 flex-shrink-0' aria-hidden='true' />
        <div className='text-sm'>
          <p className='font-semibold uppercase tracking-wide'>Banned profile, visible only to mods</p>
          { user.banEndDate && <p className='mt-0.5 text-red-100'>Ban end date: {new Date(user.banEndDate).toLocaleString()}</p> }
        </div>
      </div>
    )}

    <div className='grid grid-cols-3 gap-y-4 sm:gap-x-5'>
      <section className='col-span-3 sm:col-span-2'>
        {/* Header: cover, avatar, and the five things a producer needs. */}
        {/* No overflow-hidden here: it clipped the options menu. The cover
            rounds its own top corners instead. */}
        <div className='relative z-10 rounded-2xl bg-white shadow-sm dark:bg-gray-800'>
          <img className='h-36 w-full rounded-t-2xl object-cover sm:h-48' src={user.coverPublicImageUrl} alt='' />
          <div className='px-4 pb-4 sm:px-6'>
            <div className='-mt-12 flex items-end justify-between gap-3 sm:-mt-16'>
              <img className='h-24 w-24 rounded-xl bg-gray-200 object-cover ring-4 ring-white dark:ring-gray-800 sm:h-32 sm:w-32' src={user.largeImageUrl} alt={user.displayName} />
              <div className='flex items-center gap-x-2 pb-1'>
                { isOwner
                  ? <Link to='/profile' className='inline-flex items-center gap-x-1 rounded-full bg-cccblue px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-cccblue-alt hover:text-white'><PencilIcon className='h-4 w-4' /> Edit profile</Link>
                  : <>
                    <DirectMessageModal recipient={user} renderTrigger={(open) => (
                      <button type='button' onClick={open} className='inline-flex items-center gap-x-1 rounded-full border border-gray-300 bg-white px-4 py-2 text-sm font-semibold text-gray-800 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-cccblue focus:ring-offset-2 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100 dark:hover:bg-gray-700'>
                        <SVGIcon name='icon-messages' className='h-5 w-5' /> <span className='hidden sm:inline'>Message</span>
                      </button>
                    )} />
                    <button type='button' onClick={follow} aria-pressed={following}
                      className={classNames('inline-flex items-center gap-x-1 rounded-full px-4 py-2 text-sm font-semibold shadow-sm focus:outline-none focus:ring-2 focus:ring-cccblue focus:ring-offset-2',
                        following ? 'bg-cccpurple text-white hover:bg-cccpurple-alt' : 'border border-gray-300 bg-white text-gray-800 hover:bg-gray-50 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100 dark:hover:bg-gray-700')}>
                      { following ? <CheckCircleIcon className='h-4 w-4' /> : <SVGIcon name='icon-followers' className='h-5 w-5' /> }
                      <span className='hidden sm:inline'>{ following ? 'Following' : 'Follow' }</span>
                    </button>
                    <RecommendationModal user={user} renderTrigger={(open) => (
                      <button type='button' onClick={open} className='inline-flex items-center gap-x-1 rounded-full border border-gray-300 bg-white px-4 py-2 text-sm font-semibold text-gray-800 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-cccblue focus:ring-offset-2 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100 dark:hover:bg-gray-700'>
                        <SVGIcon name='icon-premium-only' className='h-5 w-5' /> <span className='hidden sm:inline'>Recommend</span>
                      </button>
                    )} />
                    <MoreMenu user={user} blocking={blocking} />
                  </> }
              </div>
            </div>

            <div className='mt-3 min-w-0'>
              <div className='flex flex-wrap items-center gap-x-2 gap-y-1'>
                <h1 className='text-2xl font-bold leading-tight text-gray-900 dark:text-white sm:text-3xl'>{user.displayName}</h1>
                { user.canShowOnline && user.isOnline && <span className='inline-flex items-center gap-x-1 rounded-full bg-green-100 px-2 py-0.5 text-xs font-semibold text-green-800 dark:bg-green-900/40 dark:text-green-100'><span className='h-1.5 w-1.5 rounded-full bg-green-500' /> Online</span> }
                { user.isAvailableWork && <span className='inline-flex items-center rounded-full bg-cccorange px-2.5 py-0.5 text-xs font-bold text-white'>Available for work</span> }
                { user.isStaff && <span className='inline-flex items-center rounded-full bg-gradient-to-r from-cccpurple via-cccblue to-cccorange px-2.5 py-0.5 text-xs font-bold text-white'>CCC Staff</span> }
              </div>
              <div className='text-sm text-gray-500 dark:text-gray-400'>@{user.username}</div>
              { kinds.length > 0 && <div className='mt-2 flex flex-wrap gap-1.5'>
                { kinds.map((k) => <span key={k} className='inline-flex items-center gap-x-1 rounded-full bg-gray-100 py-0.5 pl-1 pr-2.5 text-xs font-semibold text-gray-800 dark:bg-gray-700 dark:text-gray-100'><SVGIcon name={KIND_ICONS[k]} className='h-5 w-5' />{KIND_LABELS[k]}</span>) }
              </div> }
              { plain(user.headline).trim() && <p className='mt-2 text-base text-gray-800 dark:text-gray-200' dangerouslySetInnerHTML={{ __html: user.headline }} /> }
              <div className='mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-gray-500 dark:text-gray-400'>
                { user.location && <span className='inline-flex items-center gap-x-1'><LocationMarkerIcon className='h-4 w-4' />{user.location}</span> }
                <button type='button' onClick={followersModal.openModal} className='hover:text-cccblue'><b className='text-gray-900 dark:text-white'>{user.followersCount}</b> followers</button>
                <button type='button' onClick={followingModal.openModal} className='hover:text-cccblue'><b className='text-gray-900 dark:text-white'>{user.followingCount}</b> following</button>
              </div>
            </div>
          </div>
        </div>

        <InContentAd />

        {/* Tabs */}
        <div className='mt-4 border-b border-gray-200 dark:border-gray-700'>
          <nav className='-mb-px flex gap-x-1' aria-label='Profile sections'>
            { TABS.map((tab) => {
              const selected = tab === currentTab
              return <button key={tab} type='button' onClick={() => setTab(tab)} aria-current={selected ? 'page' : undefined}
                className={classNames('whitespace-nowrap rounded-t-md border-b-2 px-3 py-2 text-sm font-medium focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-cccblue',
                  selected ? 'border-cccblue text-cccblue' : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700 dark:text-gray-300 dark:hover:text-white')}>
                {tab}
                { counts[tab] > 0 && <span className={classNames('ml-2 rounded-full px-2 py-0.5 text-xs font-semibold', selected ? 'bg-cccblue text-white' : 'bg-gray-200 text-gray-800 dark:bg-gray-600 dark:text-gray-100')}>{counts[tab]}</span> }
              </button>
            })}
          </nav>
        </div>

        <div className='mt-4'>
          { currentTab === 'Profile' && <PublicProfileGeneral /> }
          { currentTab === 'Submissions' && <PublicProfileSubmissions /> }
          { currentTab === 'Projects' && <PublicProjects /> }
        </div>
        <MobileAd adID={109} />
      </section>
      <Sidebar />
    </div>

    <Modal isOpen={followersModal.isOpen} closeModal={followersModal.closeModal} title='Followers'>
      <PublicFollowers />
    </Modal>
    <Modal isOpen={followingModal.isOpen} closeModal={followingModal.closeModal} title='Following'>
      <PublicFollowings />
    </Modal>
  </div>
}

export default PublicProfile
